import { useEffect, useRef } from 'react';
import { Ic } from './Icon';
import { gsap, nf, prefersReducedMotion, ScrollTrigger } from './motion';
import type { Lang, LandingContent } from './content';

interface Props {
  c: LandingContent;
  lang: Lang;
  onOpenDemo: () => void;
}

const HERO_ROUTE = 'M60 420 C120 400, 150 330, 220 300 S330 260, 420 240 S500 210, 540 250';

export function HeroSection({ c, lang, onOpenDemo }: Props) {
  const root = useRef<HTMLElement>(null);
  const spdRef = useRef<HTMLSpanElement>(null);
  const entered = useRef(false);

  /* Entrance choreography: full timeline on first mount, instant reveal after a language switch. */
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const reduce = prefersReducedMotion();
    const ctx = gsap.context(() => {
      if (reduce || entered.current) {
        gsap.set(['.hero-anim', '#heroTitle .w'], { opacity: 1, clearProps: 'transform' });
        return;
      }
      entered.current = true;
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.fromTo('#heroBadge', { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.6 }, 0.1)
        .fromTo('#heroTitle .w', { opacity: 0, y: 34, rotateX: -30 }, { opacity: 1, y: 0, rotateX: 0, duration: 0.9, stagger: 0.045 }, 0.2)
        .fromTo('#heroLead', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7 }, 0.75)
        .fromTo('#heroCta', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7 }, 0.9)
        .fromTo('#heroTrust', { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.6 }, 1.05)
        .fromTo(
          '#heroVisual',
          { opacity: 0, y: 40, scale: 0.94, rotateY: lang === 'ar' ? 6 : -6 },
          { opacity: 1, y: 0, scale: 1, rotateY: 0, duration: 1.2, ease: 'expo.out' },
          0.45,
        )
        .fromTo('.chip', { opacity: 0, y: 18, scale: 0.9 }, { opacity: 1, y: 0, scale: 1, duration: 0.6, stagger: 0.12, ease: 'back.out(1.6)' }, 1.1);
    }, el);
    return () => ctx.revert();
  }, [lang]);

  /* Looping motion: floating chips, route draw + truck, geofence toast, parallax. Mount-only. */
  useEffect(() => {
    const el = root.current;
    if (!el || prefersReducedMotion()) return;
    const ctx = gsap.context(() => {
      gsap.to('.chip-speed', { y: -8, duration: 3.2, yoyo: true, repeat: -1, ease: 'sine.inOut', delay: 1.8 });
      gsap.to('.chip-fuel', { y: -10, duration: 3.8, yoyo: true, repeat: -1, ease: 'sine.inOut', delay: 2.1 });
      gsap.to('.chip-temp', { y: -7, duration: 3.5, yoyo: true, repeat: -1, ease: 'sine.inOut', delay: 2.4 });

      const route = el.querySelector<SVGPathElement>('#route')!;
      const len = route.getTotalLength();
      gsap.set(route, { strokeDasharray: len, strokeDashoffset: len });
      gsap.set('#truck', { opacity: 0 });
      const drive = gsap.timeline({ repeat: -1, repeatDelay: 1.6, delay: 1.4 });
      drive
        .to(route, { strokeDashoffset: 0, duration: 2.2, ease: 'power2.inOut' })
        .set('#truck', { opacity: 1 }, 0.05)
        .to(
          '#truck',
          {
            duration: 2.2,
            ease: 'power2.inOut',
            motionPath: { path: '#route', align: '#route', alignOrigin: [0.5, 0.5] },
            onUpdate() {
              if (spdRef.current) spdRef.current.textContent = String(Math.round(84 + Math.sin(this.progress() * Math.PI * 3) * 9));
            },
          },
          0,
        )
        .fromTo('#fenceToast', { opacity: 0, y: -8 }, { opacity: 1, y: 0, duration: 0.45, ease: 'back.out(1.7)' }, 2.05)
        .to('#fenceToast', { opacity: 0, y: -6, duration: 0.4 }, 4.2)
        .to('#truck', { opacity: 0, duration: 0.3 }, 4.4)
        .set(route, { strokeDashoffset: len }, 4.7);

      gsap.to('.fence-pulse', { attr: { r: 72 }, opacity: 0, duration: 2, repeat: -1, ease: 'power1.out' });
      gsap.to('.truck-pulse', { attr: { r: 30 }, opacity: 0, duration: 1.4, repeat: -1, ease: 'power1.out' });
      gsap.to('#v2', { y: 3, duration: 2, yoyo: true, repeat: -1, ease: 'sine.inOut' });
      gsap.to('#v3', { x: -40, duration: 6, yoyo: true, repeat: -1, ease: 'sine.inOut' });

      gsap.to('#heroVisual', { y: -40, ease: 'none', scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: 0.8 } });
    }, el);
    return () => {
      ctx.revert();
      ScrollTrigger.refresh();
    };
  }, []);

  const [l1, l2, l3] = c.hero.title;
  const words = (s: string) =>
    s.split(' ').map((w, i) => (
      <span className="w" key={`${w}-${i}`}>
        {w}{' '}
      </span>
    ));

  return (
    <section className="hero" ref={root}>
      <div className="lp-container">
        <div className="hero-grid">
          <div>
            <div className="hero-badge hero-anim" id="heroBadge">
              <span className="dot" />
              <span>{c.hero.badge}</span>
            </div>
            <h1 id="heroTitle">
              {words(l1)}
              <br />
              <span className="w grad">{l2}</span>
              <br />
              {words(l3)}
            </h1>
            <p className="lead hero-anim" id="heroLead">
              {c.hero.sub}
            </p>
            <div className="hero-cta hero-anim" id="heroCta">
              <button type="button" className="lp-btn lp-btn-primary lp-btn-lg" onClick={onOpenDemo}>
                <Ic name="zap" />
                <span>{c.hero.cta1}</span>
                <Ic name="arrow" flip />
                <span className="shine" />
              </button>
              <a className="lp-btn lp-btn-ghost lp-btn-lg" href="#live">
                <Ic name="play" />
                <span>{c.hero.cta2}</span>
              </a>
            </div>
            <ul className="hero-trust hero-anim" id="heroTrust">
              {[c.hero.t1, c.hero.t2, c.hero.t3].map((t) => (
                <li key={t}>
                  <Ic name="check" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="hero-visual hero-anim" id="heroVisual">
            <div className="map-card">
              <div className="map-topbar">
                <span className="lp-pill">
                  <span className="live" />
                  <span>{c.hero.map.live}</span>
                </span>
                <span className="lp-pill">
                  <Ic name="radar" className="i sm" />
                  <span>{c.hero.map.route}</span>
                </span>
              </div>
              <HeroMap fence={c.hero.map.fence} />
              <div className="toast" id="fenceToast">
                <span className="ic">
                  <Ic name="check" />
                </span>
                <span>
                  <span>{c.hero.map.toast}</span>
                  <small>{c.hero.map.toastSub}</small>
                </span>
              </div>
            </div>
            <div className="chip chip-speed">
              <span className="ic">
                <Ic name="gauge" />
              </span>
              <span>
                <span className="k">{c.hero.map.speed}</span>
                <span className="v">
                  <span ref={spdRef}>84</span> <span>{c.hero.map.kmh}</span>
                </span>
              </span>
            </div>
            <div className="chip chip-fuel">
              <span className="ic">
                <Ic name="fuel" />
              </span>
              <span>
                <span className="k">{c.hero.map.fuel}</span>
                <span className="v">78% · 312 L</span>
              </span>
            </div>
            <div className="chip chip-temp">
              <span className="ic">
                <Ic name="temp" />
              </span>
              <span>
                <span className="k">{c.hero.map.temp}</span>
                <span className="v" dir="ltr">
                  -18.4 °C
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="stats">
          {c.hero.stats.map((s, i) => (
            <div className="stat" data-reveal key={`${lang}-${i}`} style={{ '--d': `${i * 80}ms` } as React.CSSProperties}>
              <b>
                <span className="cnt" data-v={s.v} data-dec={s.dec ?? 0}>
                  {s.dec ? s.v.toFixed(s.dec) : nf.format(s.v)}
                </span>
                {s.suf ?? ''}
              </b>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HeroMap({ fence }: { fence: string }) {
  return (
    <svg viewBox="0 0 640 520" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
      <rect width="640" height="520" fill="var(--surface)" />
      <rect width="640" height="520" fill="url(#mgrid)" style={{ color: 'var(--text)' }} />
      <g fill="var(--surface-3)" stroke="var(--border)" strokeWidth="1">
        <rect x="40" y="70" width="110" height="70" rx="6" />
        <rect x="170" y="60" width="80" height="90" rx="6" />
        <rect x="420" y="90" width="150" height="60" rx="6" />
        <rect x="60" y="300" width="130" height="90" rx="6" />
        <rect x="470" y="330" width="120" height="110" rx="6" />
        <rect x="250" y="380" width="150" height="70" rx="6" />
        <rect x="300" y="200" width="90" height="60" rx="6" />
      </g>
      <g fill="none" stroke="var(--border-strong)" strokeWidth="10" strokeLinecap="round" strokeOpacity=".7">
        <path d="M0 180 H640" />
        <path d="M0 470 H640" />
        <path d="M220 0 V520" />
        <path d="M420 0 V520" />
        <path d="M0 40 C120 60, 200 140, 320 170" />
      </g>
      <g fill="none" stroke="var(--surface)" strokeWidth="2" strokeDasharray="14 12" strokeOpacity=".9">
        <path d="M0 180 H640" />
        <path d="M0 470 H640" />
        <path d="M220 0 V520" />
        <path d="M420 0 V520" />
      </g>
      <g transform="translate(540 250)">
        <circle r="70" fill="url(#fence)" />
        <circle r="52" fill="none" stroke="#a3e635" strokeWidth="2" strokeDasharray="6 6" opacity=".8" />
        <circle className="fence-pulse" r="52" fill="none" stroke="#a3e635" strokeWidth="2" opacity=".6" />
        <rect x="-42" y="-14" width="84" height="28" rx="6" fill="var(--surface)" stroke="var(--border-strong)" />
        <text x="0" y="5" textAnchor="middle" fontSize="12" fontWeight="600" fill="var(--text-2)" fontFamily="Readex Pro, sans-serif">
          {fence}
        </text>
      </g>
      <path d={HERO_ROUTE} fill="none" stroke="var(--accent)" strokeOpacity=".18" strokeWidth="10" strokeLinecap="round" />
      <path id="route" d={HERO_ROUTE} fill="none" stroke="url(#routeGrad)" strokeWidth="4" strokeLinecap="round" filter="url(#glowF)" />
      <g transform="translate(60 420)">
        <circle r="7" fill="var(--surface)" stroke="#34d399" strokeWidth="3" />
      </g>
      <g id="v2" transform="translate(150 110)">
        <circle r="16" fill="#fbbf24" fillOpacity=".18" />
        <circle r="6" fill="#fbbf24" stroke="var(--surface)" strokeWidth="2" />
      </g>
      <g id="v3" transform="translate(330 460)">
        <circle r="16" fill="#34d399" fillOpacity=".18" />
        <circle r="6" fill="#34d399" stroke="var(--surface)" strokeWidth="2" />
      </g>
      <g id="truck">
        <circle r="22" fill="#a3e635" fillOpacity=".18" className="truck-pulse" />
        <circle r="13" fill="var(--surface)" stroke="#a3e635" strokeWidth="3" />
        <g transform="translate(-7 -7) scale(.6)" stroke="var(--text)" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
          <path d="M15 18H9" />
          <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
          <circle cx="17" cy="18" r="2" />
          <circle cx="7" cy="18" r="2" />
        </g>
      </g>
    </svg>
  );
}
